/* eslint-disable no-use-before-define */
import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useFieldArray, useForm } from 'react-hook-form';
import { useGetWidgetConfigQuery, useUpdateWidgetColorsMutation, useUpdateWidgetContentMutation } from '../../../store/widget/widgetConfigApi';
import { useGetCurrentUserQuery } from '../../../store/user/userApi';
import SearchInput from '../../../shared/chat-bar/search-input';

type SettingsSection =
	| 'account-settings'
	| 'ai-agents'
	| 'billing'
	| 'widget-customisation'
	| 'widget-installation';

type WidgetContentTab = 'brand-colors' | 'content';

interface HomepageProps {
	initialSection?: SettingsSection;
}

interface WidgetQuestion {
	question: string;
}

interface WidgetCustomizationFormValues {
	primaryColor: string;
	secondaryColor: string;
	textColor: string;
	welcomeMessage: string;
	questions: WidgetQuestion[];
}

const SETTINGS_SECTIONS: { id: SettingsSection; label: string }[] = [
	{ id: 'account-settings', label: 'Account Settings' },
	{ id: 'ai-agents', label: 'AI Agents' },
	{ id: 'billing', label: 'Billing' },
	{ id: 'widget-customisation', label: 'Widget Customization' },
	{ id: 'widget-installation', label: 'Widget Installation' },
];

const DEFAULT_WIDGET_CLIENT_ID = '';

const Homepage: React.FC<HomepageProps> = ({ initialSection }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const params = useParams<{ agent?: string }>();

	const inferredSection: SettingsSection = useMemo(() => {
		if (initialSection) return initialSection;
		if (location.pathname.includes('account-settings')) return 'account-settings';
		if (location.pathname.includes('ai-agents')) return 'ai-agents';
		if (location.pathname.includes('billing')) return 'billing';
		if (location.pathname.includes('widget-installation')) return 'widget-installation';
		if (location.pathname.includes('widget-customisation')) return 'widget-customisation';
		return initialSection || 'widget-customisation';
	}, [initialSection, location.pathname]);

	const searchParams = new URLSearchParams(location.search);
	const mfeOnlyWidgetCustomisation = searchParams.get('mfe') === '1';
	const selectedAgentId = params.agent || searchParams.get('agentId');
	const hasSelectedAgent = !!selectedAgentId;
	const agentId = selectedAgentId ?? undefined;
	const safeAgentId = agentId || '';
	const { data: currentUser } = useGetCurrentUserQuery();
	const clientId = currentUser?.data?.companyData?.sub;

	const {
		data: widgetConfig,
		isFetching: isWidgetConfigLoading,
		isError: isWidgetConfigError,
	} = useGetWidgetConfigQuery({ agentId: safeAgentId, clientId }, {
		skip: !agentId || !clientId,
	});

	const [updateColors] = useUpdateWidgetColorsMutation();
	const [updateContent] = useUpdateWidgetContentMutation();
	const [logoFile, setLogoFile] = useState<File | null>(null);
	const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
	const [logoCleared, setLogoCleared] = useState(false);
	const [initialFormValues, setInitialFormValues] =
		useState<WidgetCustomizationFormValues | null>(null);
	const [initialLogoUrl, setInitialLogoUrl] = useState<string | null>(null);

	const [activeSection, setActiveSection] = useState<SettingsSection>(inferredSection);
	const [activeWidgetTab, setActiveWidgetTab] = useState<WidgetContentTab>('brand-colors');
	const [copied, setCopied] = useState(false);

	useEffect(() => {
		setActiveSection(inferredSection);
	}, [inferredSection]);

	const {
		register,
		control,
		handleSubmit,
		watch,
		setValue,
		reset,
		formState: { isSubmitting },
	} = useForm<WidgetCustomizationFormValues>({
		defaultValues: {
			primaryColor: '#00330F',
			secondaryColor: '#FAFAFA',
			textColor: '#FFFFFF',
			welcomeMessage: 'Hi, I am {{agent_name}} an AI Assistant.',
			questions: [
				{ question: 'Are there any available rooms?' },
				{ question: 'I am having some trouble with booking on the site' },
				{ question: 'I would like to book a room' },
			],
		},
	});

	const agentDisplayName = useMemo(() => {
		const raw = (widgetConfig as any)?.agent || agentId || 'leo';
		if (!raw) return 'Agent';
		const s = String(raw).replace(/agent-|_/g, ' ').trim();
		return s.charAt(0).toUpperCase() + s.slice(1);
	}, [agentId, widgetConfig]);

	const agentAvatarUrl = useMemo(() => {
		const raw = (widgetConfig as any)?.agent || agentId || 'leo';
		const agent = String(raw).replace('agent-', '').toLowerCase();
		const known: Record<string, string> = {
			leo: '/assets/agents/leo-agent.svg',
			sofia: '/assets/agents/sofia-agent.svg',
			charles: '/assets/agents/charles-agent.svg',
			sage: '/assets/agents/sage-agent-icon.svg',
			claire: '/assets/agents/claire-agent-icon.svg',
		};
		return known[agent] || '/assets/agents/leo-agent.svg';
	}, [agentId, widgetConfig]);

	const ensureAgentPlaceholder = (value: string) => {
		const replaced = (value || '').replace(/\{\{[^}]+\}\}/g, '{{agent_name}}');
		if (replaced.includes('{{agent_name}}')) return replaced;
		if (!replaced.trim()) return 'Hi, I am {{agent_name}} an AI Assistant.';
		return `Hi, I am {{agent_name}} an AI Assistant.\n\n${replaced}`;
	};

	const isEuClient = useMemo(() => {
		try {
			const lang = (navigator.language || '').toUpperCase();
			const region = lang.includes('-') ? lang.split('-')[1] : '';
			const eu = new Set([
				'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
				'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES',
				'SE',
			]);

			return eu.has(region);
		} catch {
			return false;
		}
	}, []);

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'questions',
	});

	useEffect(() => {
		if (!widgetConfig) return;
		const config: any = widgetConfig;

		const mappedQuestions =
			Array.isArray(config.questions) && config.questions.length > 0
				? config.questions.map((q: any) => ({ question: q?.question ?? '' }))
				: undefined;

		const nextInitial: WidgetCustomizationFormValues = {
			primaryColor: config.primaryColor || '#00330F',
			secondaryColor: config.secondaryColor || '#FAFAFA',
			textColor: config.textColor || '#FFFFFF',
			welcomeMessage:
				ensureAgentPlaceholder(
					config.welcomeMessage || 'Hi, I am {{agent_name}} an AI Assistant.',
				),
			questions:
				mappedQuestions ??
				[
					{ question: 'Are there any available rooms?' },
					{ question: 'I am having some trouble with booking on the site' },
					{ question: 'I would like to book a room' },
				],
		};

		// EU AI Act: keep first interaction disclosure non-editable.
		if (isEuClient) {
			nextInitial.welcomeMessage = 'Hi, I am {{agent_name}} an AI Assistant.';
		}

		setInitialFormValues(nextInitial);
		reset(nextInitial);

		const apiLogoUrl = config.logoUrl || null;
		setInitialLogoUrl(apiLogoUrl);
		if (!logoCleared) setLogoPreviewUrl(apiLogoUrl);
	}, [widgetConfig, reset, logoCleared, isEuClient]);

	const onSubmit = async (values: WidgetCustomizationFormValues) => {
		if (!agentId || !clientId) return;

		await updateColors({
			agent: agentId,
			clientId,
			primaryColor: values.primaryColor,
			secondaryColor: values.secondaryColor,
			textColor: values.textColor,
			file: logoFile ?? undefined,
		});

		const enforcedWelcome = isEuClient
			? 'Hi, I am {{agent_name}} an AI Assistant.'
			: ensureAgentPlaceholder(values.welcomeMessage);

		await updateContent({
			agent: agentId,
			clientId,
			welcomeMessage: enforcedWelcome,
			questions: values.questions.map((q, index) => ({
				index,
				question: q.question,
			})),
		});
	};

	const handleSectionClick = (section: SettingsSection) => {
		setActiveSection(section);
		if (!agentId) return;

		const sectionToPath: Record<SettingsSection, string> = {
			'account-settings': 'account-settings',
			'ai-agents': 'ai-agents',
			'billing': 'billing',
			'widget-customisation': 'widget-customisation',
			'widget-installation': 'widget-installation',
		};

		navigate(
			`/dashboard/${agentId}/${sectionToPath[section]}${location.search}`,
			{ replace: false },
		);
	};

	const renderComingSoon = (label: string) => (
		<div className="mt-8 rounded-2xl border border-gray-200 bg-white p-8 shadow-sm dark:border-borderColor-darkMode dark:bg-bgColor-darkMode">
			<h2 className="mb-2 text-xl font-semibold text-gray-900 dark:text-white">{label}</h2>
			<p className="text-sm text-gray-500 dark:text-gray-300">
				This section is currently in development. Stay tuned, exciting updates
				are coming soon.
			</p>
		</div>
	);

	const htmlSnippet = useMemo(() => {
		const resolvedClientId = clientId || DEFAULT_WIDGET_CLIENT_ID;
		const resolvedAgentId = agentId || '';
		return `<script
  src="${process.env.REACT_APP_BASE_URL}/widget.js"
  data-agent-id="${resolvedAgentId}"
  data-client-id="${resolvedClientId}"
  async
></script>`;
	}, [agentId, clientId]);

	const renderCodeSnippet = () => (
		<div className="mt-5 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm dark:border-borderColor-darkMode dark:bg-bgColor-darkMode">
			<h2 className="text-lg font-semibold text-gray-900 dark:text-white">Widget Installation</h2>
			{isWidgetConfigLoading ? (
				<p className="mt-3 text-sm text-gray-500 dark:text-gray-300">
					Checking widget configuration...
				</p>
			) : (isWidgetConfigError || !widgetConfig) ? (
				<div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200">
					Widget Installation is available after you save Widget Configuration first.
				</div>
			) : (
				<>
					<p className="mt-2 text-sm text-gray-500 dark:text-gray-300">
						Copy and paste this HTML snippet into your website to load the chat widget.
					</p>

					<div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-borderColor-darkMode dark:bg-black">
						<pre className="overflow-x-auto whitespace-pre-wrap break-words text-sm text-gray-800 dark:text-gray-200">
							<code>{htmlSnippet}</code>
						</pre>
					</div>

					<div className="mt-4 flex items-center justify-end">
						<button
							type="button"
							onClick={async () => {
								try {
									await navigator.clipboard.writeText(htmlSnippet);
									setCopied(true);
									setTimeout(() => setCopied(false), 1500);
								} catch {
									setCopied(false);
								}
							}}
							className="inline-flex items-center rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-black dark:border dark:border-white"
						>
							{copied ? 'Copied' : 'Copy snippet'}
						</button>
					</div>
				</>
			)}
		</div>
	);

	const renderWidgetConfiguration = () => (
		<div className="mt-5 grid gap-8 2xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
			<form
				onSubmit={handleSubmit(onSubmit)}
				className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm dark:border-borderColor-darkMode dark:bg-bgColor-darkMode"
			>
				<div className="mb-2 flex flex-wrap items-center justify-between gap-3">
					<div>
						<h2 className="text-lg font-semibold text-gray-900 dark:text-white">
							Widget Configuration
						</h2>
						<div className="inline-flex rounded-full bg-gray-100 p-1 text-xs font-medium text-gray-600 my-3 dark:bg-black dark:text-gray-200">
							<span
								className={`cursor-pointer rounded-full px-3 py-1 ${activeWidgetTab === 'brand-colors'
										? 'bg-white text-gray-900 shadow-sm dark:bg-primary-default dark:text-white'
										: ''
									}`}
								onClick={() => setActiveWidgetTab('brand-colors')}
							>
								Brand Colors
							</span>
							<span
								className={`cursor-pointer rounded-full px-3 py-1 ${activeWidgetTab === 'content'
										? 'bg-white text-gray-900 shadow-sm dark:bg-primary-default dark:text-white'
										: ''
									}`}
								onClick={() => setActiveWidgetTab('content')}
							>
								Content
							</span>
						</div>
					</div>
				</div>

				{activeWidgetTab === 'brand-colors' && (
					<div className="space-y-6 border border-gray-100 rounded-2xl p-4 dark:border-borderColor-darkMode">
						<div>
							<h3 className="mb-2 text-sm font-medium text-gray-900 dark:text-white">
								Colors
							</h3>
							<p className="mb-4 text-xs text-gray-500 dark:text-gray-300">
								Customize your widget&apos;s color scheme to match your
								brand.
							</p>
							<div className="grid gap-4">
								<div>
									<label className="mb-1 block text-xs font-medium text-gray-700">
										Primary Color
									</label>
									<div className="flex items-center gap-3">
										<input
											type="color"
											className="h-10 w-10 rounded border border-gray-200 bg-transparent dark:border-gray-700"
											value={watch('primaryColor')}
											onChange={(e) => setValue('primaryColor', e.target.value)}
										/>
										<input
											type="text"
											className="block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-green-700 focus:outline-none focus:ring-1 focus:ring-green-700 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
											{...register('primaryColor')}
										/>
									</div>
									<p className="mt-1 text-xs text-gray-400 dark:text-gray-300">
										Used for buttons, header, and user messages.
									</p>
								</div>
								<div>
									<label className="mb-1 block text-xs font-medium text-gray-700">
										Secondary Color
									</label>
									<div className="flex items-center gap-3">
										<input
											type="color"
											className="h-10 w-10 rounded border border-gray-200 bg-transparent dark:border-gray-700"
											value={watch('secondaryColor')}
											onChange={(e) => setValue('secondaryColor', e.target.value)}
										/>
										<input
											type="text"
											className="block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-green-700 focus:outline-none focus:ring-1 focus:ring-green-700 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
											{...register('secondaryColor')}
										/>
									</div>
									<p className="mt-1 text-xs text-gray-400 dark:text-gray-300">
										Used for AI agents message bubbles.
									</p>
								</div>
								<div>
									<label className="mb-1 block text-xs font-medium text-gray-700">
										Text Color
									</label>
									<div className="flex items-center gap-3">
										<input
											type="color"
											className="h-10 w-10 rounded border border-gray-200 bg-transparent dark:border-gray-700"
											value={watch('textColor')}
											onChange={(e) => setValue('textColor', e.target.value)}
										/>
										<input
											type="text"
											className="block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-green-700 focus:outline-none focus:ring-1 focus:ring-green-700 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
											{...register('textColor')}
										/>
									</div>
									<p className="mt-1 text-xs text-gray-400">
										Text color on primary color backgrounds.
									</p>
								</div>
							</div>
						</div>

						<div className="border-t border-gray-100 pt-4">
							<h3 className="mb-2 text-base font-medium text-[#0A0A0A] dark:text-white">
								Logo
							</h3>
							<p className="mb-4 text-sm md:text-base text-[#717182]">
								Add your brand logo to the chat widget it should be a square image.
							</p>
							<div className="space-y-2 text-xs text-gray-500">
								<label className="flex h-24 w-32 cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-100 dark:bg-black hover:border-gray-400 overflow-hidden mx-auto">
									{logoPreviewUrl ? (
										<img
											src={logoPreviewUrl}
											alt="Logo preview"
											className="h-full w-full object-contain"
										/>
									) : (
										<img src="/assets/chat/upload-files.svg" alt="upload logo" className="w-10 h-10" />
									)}
									<input
										type="file"
										accept="image/*"
										className="hidden"
										onChange={(e) => {
											const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
											setLogoFile(file);
											if (file) {
												const url = URL.createObjectURL(file);
												setLogoPreviewUrl(url);
												setLogoCleared(false);
											} else {
												setLogoPreviewUrl(null);
											}
										}}
									/>
								</label>
								<div className="flex items-center gap-3 justify-center">
									<p className="text-[11px] text-gray-400">
										PNG, JPG, SVG up to 2MB.
									</p>
									{(logoPreviewUrl || (widgetConfig as any)?.logoUrl) && !logoCleared && (
										<button
											type="button"
											onClick={() => {
												setLogoFile(null);
												setLogoPreviewUrl(null);
												setLogoCleared(true);
											}}
											className="text-[11px] font-medium text-red-500 hover:text-red-600"
										>
											Remove X
										</button>
									)}
								</div>
							</div>
						</div>
					</div>
				)}

				{activeWidgetTab === 'content' && (
					<div className="space-y-6">
						<div>
							<h3 className="mb-2 text-sm font-medium text-gray-900">
								Welcoming Message
							</h3>
							<p className="mb-3 text-xs text-gray-500">
								This is the first message shown when the widget is
								clicked.
							</p>
							<textarea
								className="min-h-[80px] w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-green-700 focus:outline-none focus:ring-1 focus:ring-green-700 dark:bg-black"
								value={watch('welcomeMessage')}
								disabled={isEuClient}
								onChange={(e) => {
									if (isEuClient) return;
									setValue('welcomeMessage', ensureAgentPlaceholder(e.target.value));
								}}
							/>
							{isEuClient && (
								<p className="mt-2 text-xs text-gray-500">
									This message is locked for EU clients to ensure users are clearly informed
									they are interacting with AI.
								</p>
							)}
						</div>

						<div className="border-t border-gray-100 pt-4">
							<div className="mb-3 flex items-center justify-between">
								<div>
									<h3 className="text-base font-medium text-gray-900 dark:text-white">
										Add a default topic
									</h3>
									<p className="text-base text-[#717182]">
										You can add up to 10 topics.
									</p>
								</div>
							</div>

							<div className="space-y-3 my-5">
								{fields.map((field, index) => (
									<div className="my-3" key={field.id}>
										<div className='flex flex-wrap items-center mb-2'>
											<div className="text-sm font-medium text-gray-600 dark:text-gray-200">
												Question {String(index + 1).padStart(2, '0')}
											</div>
											{fields.length > 1 && (
													<button
														type="button"
														className="text-xs text-gray-400 hover:text-red-500 ml-auto dark:text-gray-300"
														onClick={() => remove(index)}
													>
														<img src="/svg/icons/delete.svg" alt="delete" className="w-6 h-6" />
													</button>
												)}
											</div>
										<div
											key={field.id}
											className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-borderColor-darkMode dark:bg-black"
										>
											
											<input
												type="text"
												className="flex-1 rounded-md border border-transparent bg-transparent py-1 text-sm text-gray-900 dark:text-white focus:outline-none "
												{...register(`questions.${index}.question` as const)}
											/>
											
										</div>
									</div>
								))}
							</div>
							<div className='flex flex-row justify-center my-5'>
							<button
									type="button"
									className="inline-flex items-center rounded-full bg-[#2D8EFF] px-5 py-2 text-sm font-medium text-white hover:bg-[#2D8EFF]/[0.8]"
									onClick={() =>
										append({ question: '' })
									}
									disabled={fields.length >= 10}
								>
									+ Add another question
								</button>
							</div>
						</div>
					</div>
				)}

				<div className="mt-5 flex items-center justify-end gap-3">
					<button
						type="button"
						onClick={() => {
							if (initialFormValues) reset(initialFormValues);
							else reset();
							setLogoFile(null);
							setLogoCleared(false);
							setLogoPreviewUrl(initialLogoUrl);
						}}
						className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-borderColor-darkMode dark:bg-bgColor-darkMode dark:text-gray-100 dark:hover:bg-black"
					>
						Reset
					</button>
					<button
						type="submit"
						disabled={isSubmitting}
						className="inline-flex items-center rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-black disabled:cursor-not-allowed disabled:opacity-70 dark:border dark:border-white"
					>
						<img src="/assets/shared/save.svg" alt="save" className="w-4 h-4 mr-2" />
						Save Configuration
					</button>
				</div>
			</form>

			<div className="rounded-2xl border border-gray-100 bg-white p-4 md:p-6 shadow-sm overflow-hidden dark:border-borderColor-darkMode dark:bg-bgColor-darkMode">
				<div className="flex flex-col justify-center">
					<h4 className="text-[#0A0A0A] text-lg font-semibold dark:text-white">Preview</h4>
					<div className="relative mt-1 w-full rounded-md p-2 md:p-4 shadow-inner bg-gradient-to-b from-[#F9FAFB] to-[#F3F4F6] dark:from-[#0B1B1B] dark:to-[#111827]">
						<h2 className="mb-2 text-sm font-medium text-[#4A5565] dark:text-gray-200">Live Preview</h2>
						<div className="px-2 md:px-5 py-3">
							<div className="flex flex-col rounded-lg bg-white shadow-md overflow-hidden dark:bg-[#0B1B1B] dark:border dark:border-borderColor-darkMode">
								{/* Header bar */}
								<div
									className="flex items-center justify-between rounded-t-md px-4 py-3"
									style={{ backgroundColor: watch('primaryColor') }}
								>
									<div className="flex items-center gap-2">
										<div className="h-8 w-8 rounded-full bg-white/10 overflow-hidden flex items-center justify-center">
											{logoPreviewUrl ? (
												<img src={logoPreviewUrl} alt="logo" className="h-full w-full object-contain" />
											) : (
												<span className="text-xs text-white">HW</span>
											)}
										</div>
									</div>
									<div className="flex h-6 w-6 items-center justify-center rounded-full bg-white/10">
										<div className="space-y-[3px]">
											<span className="block h-[2px] w-3 rounded bg-white" />
											<span className="block h-[2px] w-3 rounded bg-white" />
											<span className="block h-[2px] w-3 rounded bg-white" />
										</div>
									</div>
								</div>

								{/* Conversation card */}
								<div className="px-4 pb-3 pt-3 bg-white dark:bg-[#0B1B1B]">
									<div className="rounded-md bg-white shadow-sm dark:bg-[#0B1B1B]">
										<div className="px-4 pt-4 min-h-[400px]">
											<p className="text-base font-semibold text-black dark:text-white">
												Start a conversation with {agentDisplayName || ''}
											</p>
											<div className="mt-3 flex items-center gap-3">
											<img src={agentAvatarUrl} className='min-w-14 rounded-full' />

												<div>
													<p className="text-sm font-natural text-[#737376] dark:text-gray-300">
														{agentDisplayName}
													</p>
													<p className="flex items-center gap-1 text-sm text-[#10B981]">
														<span className="h-2 w-2 rounded-full bg-[#10B981]" />
														Responds immediately
													</p>
												</div>
											</div>
											<p className="mt-3 text-sm text-gray-700 dark:text-gray-300">
												{watch('welcomeMessage').replaceAll('{{agent_name}}', agentDisplayName)}
											</p>
											<p className="mt-4 text-sm font-normal text-[#737376] dark:text-gray-300">
												Most common questions
											</p>
											<div className="mt-2 space-y-2 pb-4">
												{(watch('questions') || []).map((item) => (
													<div
														key={item.question}
														className="flex items-center gap-2 rounded-full px-3 py-3"
														style={{ backgroundColor: watch('primaryColor') }}
													>
														<div className="inline-flex items-center justify-center rounded-full text-[9px] text-[#00330F] w-4 h-4">
															<img src='/svg/icons/send-msg.svg' alt='send' className='w-4 h-4' />
														</div>
														<span
															className="text-xs"
															style={{ color: watch('textColor') }}
														>
															{item.question}
														</span>
													</div>
												))}
											</div>
										</div>

										{/* Input bar */}
										<div className="py-3">
											<div className="">
												<SearchInput
													placeholder="Ask Something"
													chatData={[]}
													considerDarkMode={false}
													onSendingData={() => {}}
													isWaiting={true}
												/>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);

	// Requirement: if no agent is selected, show an instruction page.
	if (!hasSelectedAgent) {
		return (
			<div className="px-4 py-2 sm:px-6 lg:px-8">
				<div className="flex min-h-[70vh] items-center justify-center">
					<div className="w-full max-w-xl rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-sm dark:bg-bgColor-darkMode dark:border-borderColor-darkMode">
						<h1 className="text-xl font-semibold text-gray-900 dark:text-white">
							Select an agent to continue
						</h1>
						<p className="mt-3 text-sm text-gray-500 dark:text-gray-300">
							Choose an agent below to configure widget colors, welcome message, and copy the integration script.
						</p>

						<div className="mt-6 flex flex-col gap-3">
							<button
								type="button"
								className="rounded-lg bg-gray-900 px-4 py-3 text-sm font-medium text-white hover:bg-black dark:border"
								onClick={() =>
									navigate(
										`/dashboard/leo/widget-customisation${location.search}`,
									)
								}
							>
								Select Leo
							</button>
							<button
								type="button"
								className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-borderColor-darkMode dark:bg-bgColor-darkMode dark:text-gray-100 dark:hover:bg-black"
								onClick={() =>
									navigate(
										`/dashboard/claire/widget-customisation${location.search}`,
									)
								}
							>
								Select Claire
							</button>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Micro-frontend mode: expose widget customization + installation only.
	if (mfeOnlyWidgetCustomisation) {
		return (
			<div className="">
				<div className="px-4 py-2 sm:px-6 lg:px-8">
					<div className="mb-6">
						<h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Widget Settings</h1>
					</div>
					<div className="mb-5 flex flex-wrap gap-3 border-gray-200 pb-5 text-base font-normal text-gray-500 dark:border-borderColor-darkMode dark:text-gray-300">
						{(['widget-customisation', 'widget-installation'] as SettingsSection[]).map((section) => {
							const isActive = activeSection === section;
							const label = section === 'widget-customisation' ? 'Widget Customization' : 'Widget Installation';
							return (
								<button
									key={section}
									type="button"
									onClick={() => handleSectionClick(section)}
									className={`relative border-b px-2 pb-2 ${isActive
										? 'border-gray-800 text-gray-800 dark:border-white/20 dark:text-white'
										: 'border-transparent text-gray-600 dark:text-gray-300'}`}
								>
									{label}
								</button>
							);
						})}
					</div>
					{activeSection === 'widget-installation'
						? renderCodeSnippet()
						: renderWidgetConfiguration()}
				</div>
			</div>
		);
	}

	return (
		<div className="">
			<div className="px-2 py-2 sm:px-6 lg:px-8">
				<div className="mb-6 flex items-center justify-between">
					<div>
						<h1 className="text-base font-medium text-gray-600 dark:text-gray-200 flex items-center">
							<NavLink to="/dashboard">
								<img src="/assets/shared/go-back.svg" alt="go back" className="w-3 h-3 mr-2" />
							</NavLink>
							Settings
						</h1>
					</div>
				</div>

				<div className="flex flex-wrap gap-3 border-gray-200 pb-5 text-base font-normal text-gray-500 dark:border-borderColor-darkMode dark:text-gray-300">
					{SETTINGS_SECTIONS.map((section) => {
						const isActive = activeSection === section.id;
						const isClickable =
							section.id === 'widget-customisation' ||
							(section.id === 'widget-installation' && hasSelectedAgent);
						return (
							<button
								key={section.id}
								type="button"
								onClick={() =>
									isClickable
										? handleSectionClick(section.id)
										: setActiveSection(section.id)
								}
								className={`relative border-b px-2 pb-2 ${isActive
										? 'border-gray-800 text-gray-800 dark:border-white/20 dark:text-white'
										: 'border-transparent text-gray-600 dark:text-gray-300'
									} ${!isClickable ? 'cursor-default opacity-50' : 'cursor-pointer'}`}
								title={
									!isClickable
										? 'Select an agent from sidebar first'
										: undefined
								}
							>
								{section.label}
							</button>
						);
					})}
				</div>

				{activeSection === 'account-settings' &&
					renderComingSoon('Account Settings')}
				{activeSection === 'ai-agents' && renderComingSoon('AI Agents')}
				{activeSection === 'billing' && renderComingSoon('Billing')}
				{activeSection === 'widget-customisation' && renderWidgetConfiguration()}
				{activeSection === 'widget-installation' && renderCodeSnippet()}
			</div>
		</div>
	);
};

export default Homepage;